import { apiFetch } from './apiClient';

/**
 * All real backend calls for the app. Shapes match BACKEND_SPEC.md.
 *
 * List endpoints return `{ items: [...] }` on the wire; these helpers unwrap
 * to plain arrays for the UI.
 */

// ============================================================
// AUTH
// ============================================================

/**
 * Exchange a Google ID token for our session JWT + user object.
 * Backend verifies the Google ID token and upserts the user.
 *
 * Returns: { jwt, user: { id, email, name, picture } }
 */
export const exchangeGoogleToken = async (idToken) => {
  return apiFetch('/auth/google', {
    method: 'POST',
    body: JSON.stringify({ idToken }),
  });
};

export const logout = () => {
  localStorage.removeItem('jwt');
};

// ============================================================
// ACCOUNT GROUPS
// ============================================================

export const listAccountGroups = async () => {
  const res = await apiFetch('/account-groups');
  return res?.items || [];
};

export const getAccountGroup = async (id) => {
  return apiFetch(`/account-groups/${id}`);
};

export const createAccountGroup = async ({ name, description }) => {
  return apiFetch('/account-groups', {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
};

export const deleteAccountGroup = async (id) => {
  return apiFetch(`/account-groups/${id}`, { method: 'DELETE' });
};

// ============================================================
// ACCOUNTS
// ============================================================

export const listAccounts = async (groupId) => {
  const res = await apiFetch(`/account-groups/${groupId}/accounts`);
  return res?.items || [];
};

export const getAccount = async (id) => {
  return apiFetch(`/accounts/${id}`);
};

export const createAccount = async ({ groupId, name, description }) => {
  return apiFetch(`/account-groups/${groupId}/accounts`, {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });
};

export const deleteAccount = async (id) => {
  return apiFetch(`/accounts/${id}`, { method: 'DELETE' });
};

// ============================================================
// LOGINS (platform links)
// ============================================================

export const listLogins = async (accountId) => {
  const res = await apiFetch(`/accounts/${accountId}/logins`);
  return res?.items || [];
};

export const getLogin = async (id) => {
  return apiFetch(`/logins/${id}`);
};

/**
 * Username-only link flow (TikTok / YouTube while those OAuth integrations
 * are still pending on the BE). Instagram now uses startInstagramLink below.
 */
export const linkPlatformLogin = async ({ accountId, platform, platformUsername }) => {
  return apiFetch(`/accounts/${accountId}/logins`, {
    method: 'POST',
    body: JSON.stringify({ platform, platformUsername }),
  });
};

/**
 * Begin Instagram OAuth. Backend returns { url } pointing at Instagram's
 * authorize endpoint with our state/redirect baked in. FE sends the browser
 * there; after Instagram + our callback, user lands back on
 *   /accounts/<id>?ig_linked=1
 * or
 *   /accounts/<id>?ig_error=<reason>
 */
export const startInstagramLink = async (accountId) => {
  return apiFetch(
    `/auth/instagram/start?accountId=${encodeURIComponent(accountId)}`
  );
};

/**
 * Begin TikTok OAuth. Same contract as Instagram — backend returns { url };
 * after the round-trip the user lands back on
 *   /accounts/<id>?tt_linked=1
 * or
 *   /accounts/<id>?tt_error=<reason>
 */
export const startTikTokLink = async (accountId) => {
  return apiFetch(
    `/auth/tiktok/start?accountId=${encodeURIComponent(accountId)}`
  );
};

/**
 * Begin YouTube OAuth. Same contract as Instagram/TikTok — backend returns
 * { url }; after the round-trip the user lands back on
 *   /accounts/<id>?yt_linked=1
 * or
 *   /accounts/<id>?yt_error=<reason>
 */
export const startYouTubeLink = async (accountId) => {
  return apiFetch(
    `/auth/youtube/start?accountId=${encodeURIComponent(accountId)}`
  );
};

/**
 * Begin X (Twitter) OAuth. Same contract as the others — backend returns
 * { url }; after the round-trip the user lands back on
 *   /accounts/<id>?x_linked=1
 * or
 *   /accounts/<id>?x_error=<reason>
 */
export const startXLink = async (accountId) => {
  return apiFetch(
    `/auth/x/start?accountId=${encodeURIComponent(accountId)}`
  );
};

/**
 * Begin Facebook OAuth. Same contract as the others — backend returns
 * { url }; after the round-trip the user lands back on
 *   /accounts/<id>?fb_linked=1
 * or
 *   /accounts/<id>?fb_error=<reason>
 *
 * Note: the link binds to the first Page the authorizing user admins.
 */
export const startFacebookLink = async (accountId) => {
  return apiFetch(
    `/auth/facebook/start?accountId=${encodeURIComponent(accountId)}`
  );
};

export const unlinkLogin = async (id) => {
  return apiFetch(`/logins/${id}`, { method: 'DELETE' });
};

// ============================================================
// UPLOADS
// ============================================================

export const listUploads = async (loginId) => {
  const res = await apiFetch(`/logins/${loginId}/uploads`);
  return res?.items || [];
};

// ============================================================
// STATS AGGREGATES
// ============================================================

/**
 * Account-level rollup: totals across every login on the account, plus a
 * per-login breakdown row. See BACKEND_SPEC §5.8.1 for the full shape and
 * the invariant that `totals` equals the sum of `logins[]`.
 */
export const getAccountStats = async (accountId) => {
  return apiFetch(`/accounts/${accountId}/stats`);
};

/**
 * Group-level rollup: totals across every account in the group, plus a
 * per-account breakdown row. See BACKEND_SPEC §5.8.2.
 */
export const getAccountGroupStats = async (groupId) => {
  return apiFetch(`/account-groups/${groupId}/stats`);
};

// ============================================================
// REFRESH
// ============================================================

/**
 * Trigger an on-demand stat refresh at a given hierarchy level.
 * level: 'group' | 'account' | 'login'
 * Returns: { newUploads, loginsRefreshed, partialFailures? }
 */
export const triggerRefresh = async ({ level, id }) => {
  return apiFetch('/refresh', {
    method: 'POST',
    body: JSON.stringify({ level, id }),
  });
};
